/* Cassaforte · creazione guidata di un wallet personale.
 *
 * Tre passi invece di un modulo solo, ed e' la correzione del difetto che il
 * documento di redesign imputa alla schermata precedente («flusso confuso,
 * doppio decrypt, testo cifrato grezzo a video»): li' generazione, cifratura e
 * verifica stavano tutte nella stessa schermata, contemporaneamente, e non era
 * chiaro quale bottone scrivesse davvero a database.
 *
 * Qui l'ordine e' quello delle decisioni:
 *   1. che wallet ci metto dentro (generato o incollato);
 *   2. con che password lo chiudo;
 *   3. conferma, con scritto che cosa verra' salvato e che cosa si perde se
 *      la password si perde.
 *
 * Quello che parte nel POST e' gia' testo cifrato: la password non esce da
 * questo componente, non finisce in uno stato globale, non viene salvata e non
 * e' recuperabile. Alla chiusura ogni campo viene svuotato, riuscita o no.
 */

import { useEffect, useState } from 'react';
import { Avvertenza, Pulsante } from '../../componenti';
import { Campo } from '../../componenti/Modulo';
import { Modale } from '../../componenti/Sovrapposizioni';
import { personalWallets } from '../../api/risorse';
import { ErroreApi } from '../../api/client';
import { cifraVerificando, ErroreCifratura } from './cifratura';
import { chiaveValida, generaWallet, indirizzoValido, mnemonicaValida } from './generazione';
import { mascherato } from './dati';

/* Sotto gli 8 caratteri la cifratura piu' robusta non serve a niente: la
   password si prova tutta. Non e' una regola di stile, e' il limite sotto il
   quale il resto del file e' teatro. */
const MINIMO_PASSWORD = 8;
/* Sopra i 12 non si avvisa piu'. Non e' un blocco: e' il punto in cui
   l'avvertenza smetterebbe di dire qualcosa di utile. */
const CONSIGLIATO_PASSWORD = 12;

type Passo = 1 | 2 | 3;

interface Props {
  aperto: boolean;
  onChiudi: () => void;
  nomiEsistenti: string[];
  onCreato: (nome: string) => void;
}

export function ModuloNuovoWallet({ aperto, onChiudi, nomiEsistenti, onCreato }: Props) {
  const [passo, setPasso] = useState<Passo>(1);

  const [nome, setNome] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [chiave, setChiave] = useState('');
  const [mnemonica, setMnemonica] = useState('');
  const [generato, setGenerato] = useState(false);

  const [password, setPassword] = useState('');
  const [conferma, setConferma] = useState('');
  const [mostraPassword, setMostraPassword] = useState(false);
  const [annotata, setAnnotata] = useState(false);

  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /* Svuotare alla chiusura e non all'apertura: fra una chiusura e l'apertura
     successiva puo' passare mezz'ora, e per tutta quella mezz'ora una chiave
     privata resterebbe in memoria dentro un componente montato. */
  useEffect(() => {
    if (aperto) return;
    setPasso(1);
    setNome('');
    setIndirizzo('');
    setChiave('');
    setMnemonica('');
    setGenerato(false);
    setPassword('');
    setConferma('');
    setMostraPassword(false);
    setAnnotata(false);
    setInCorso(false);
    setErrore(null);
  }, [aperto]);

  async function genera() {
    setErrore(null);
    setInCorso(true);
    try {
      const w = await generaWallet();
      setIndirizzo(w.address);
      setChiave(w.privateKey);
      setMnemonica(w.mnemonic);
      setGenerato(true);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Generazione non riuscita.');
    } finally {
      setInCorso(false);
    }
  }

  const nomeDuplicato = nome.trim() !== '' && nomiEsistenti.some((n) => n.toLowerCase() === nome.trim().toLowerCase());

  const erroreNome = nome.trim() === '' ? null : nomeDuplicato ? 'Esiste già un wallet con questo nome.' : null;
  const erroreIndirizzo = indirizzo.trim() === '' || indirizzoValido(indirizzo) ? null : 'Non è un indirizzo Ethereum (0x più 40 cifre esadecimali).';
  const erroreChiave = chiave.trim() === '' || chiaveValida(chiave) ? null : 'Non è una chiave privata (64 cifre esadecimali, con o senza 0x).';
  const erroreMnemonica = mnemonica.trim() === '' || mnemonicaValida(mnemonica) ? null : 'Una frase mnemonica ha 12, 15, 18, 21 o 24 parole minuscole.';

  const passo1Valido =
    nome.trim() !== '' &&
    !nomeDuplicato &&
    indirizzoValido(indirizzo) &&
    chiaveValida(chiave) &&
    (mnemonica.trim() === '' || mnemonicaValida(mnemonica));

  const errorePassword =
    password === '' || password.length >= MINIMO_PASSWORD
      ? null
      : `Almeno ${MINIMO_PASSWORD} caratteri: sotto, la cifratura non protegge da niente.`;
  const erroreConferma = conferma === '' || conferma === password ? null : 'Le due password non coincidono.';
  const passo2Valido = password.length >= MINIMO_PASSWORD && conferma === password;

  async function salva() {
    setErrore(null);
    setInCorso(true);
    try {
      /* `cifraVerificando` e non `cifra`: ogni valore viene riaperto e
         confrontato prima di partire. Quello che va a database e' l'unica
         copia, e un dato illeggibile si scoprirebbe il giorno in cui serve. */
      const dati = {
        name: nome.trim(),
        address: await cifraVerificando(indirizzo.trim(), password),
        privateKey: await cifraVerificando(chiave.trim(), password),
        ...(mnemonica.trim() !== '' ? { mnemonic: await cifraVerificando(mnemonica.trim(), password) } : {}),
      };
      await personalWallets.crea(dati);
      onCreato(dati.name);
    } catch (e) {
      if (e instanceof ErroreCifratura) setErrore(e.message);
      else if (e instanceof ErroreApi) setErrore(e.message);
      else setErrore('Salvataggio non riuscito.');
    } finally {
      setInCorso(false);
    }
  }

  const pieda =
    passo === 1 ? (
      <Pulsante variante="primario" largo disabled={!passo1Valido} onClick={() => setPasso(2)}>
        Avanti: la password
      </Pulsante>
    ) : passo === 2 ? (
      <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
        <Pulsante variante="tenue" onClick={() => setPasso(1)}>
          Indietro
        </Pulsante>
        <Pulsante variante="primario" style={{ flex: 1 }} disabled={!passo2Valido} onClick={() => setPasso(3)}>
          Avanti: conferma
        </Pulsante>
      </div>
    ) : (
      <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
        <Pulsante variante="tenue" disabled={inCorso} onClick={() => setPasso(2)}>
          Indietro
        </Pulsante>
        <Pulsante variante="primario" style={{ flex: 1 }} disabled={!annotata || inCorso} onClick={salva}>
          {inCorso ? 'Cifro e salvo…' : 'Cifra e salva'}
        </Pulsante>
      </div>
    );

  return (
    <Modale
      aperta={aperto}
      onChiudi={onChiudi}
      titolo="Nuovo wallet personale"
      sottotitolo={`Passo ${passo} di 3 · ${passo === 1 ? 'il wallet' : passo === 2 ? 'la password' : 'conferma'}`}
      pieda={pieda}
      larga
    >
      <div className="sb-colonna" style={{ gap: 'var(--sb-s-5)' }}>
        {errore && <Avvertenza livello="grave">{errore}</Avvertenza>}

        {passo === 1 && (
          <>
            <Avvertenza livello="nota">
              Questa è una cassaforte: nessun bot legge questi wallet e il server non può
              aprirli. Serve a custodire un wallet, non a farci operare un tradebot: per quello
              c'è la schermata Wallet.
            </Avvertenza>

            <Campo
              etichetta="Nome"
              testuale
              suggerimento="come lo riconosci nella lista"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              errore={erroreNome}
              autoComplete="off"
            />

            <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
              <Pulsante onClick={genera} disabled={inCorso}>
                {inCorso ? 'Genero…' : 'Genera un wallet nuovo'}
              </Pulsante>
              <span className="sb-nota">oppure incolla qui sotto un wallet che hai già</span>
            </div>

            {generato && (
              <Avvertenza livello="cautela">
                Wallet generato in questa pagina, non è mai uscito dal browser. Finchè non
                lo salvi non esiste da nessuna parte: se chiudi adesso, lo perdi.
              </Avvertenza>
            )}

            <Campo
              etichetta="Indirizzo pubblico"
              testuale
              value={indirizzo}
              onChange={(e) => setIndirizzo(e.target.value)}
              errore={erroreIndirizzo}
              autoComplete="off"
              spellCheck={false}
            />
            <Campo
              etichetta="Chiave privata"
              testuale
              type={mostraPassword ? 'text' : 'password'}
              value={chiave}
              onChange={(e) => setChiave(e.target.value)}
              errore={erroreChiave}
              autoComplete="off"
              spellCheck={false}
            />
            <Campo
              etichetta="Frase mnemonica"
              testuale
              suggerimento="facoltativa: senza, il wallet si recupera solo con la chiave"
              type={mostraPassword ? 'text' : 'password'}
              value={mnemonica}
              onChange={(e) => setMnemonica(e.target.value)}
              errore={erroreMnemonica}
              autoComplete="off"
              spellCheck={false}
            />
            <Pulsante variante="nudo" onClick={() => setMostraPassword((v) => !v)}>
              {mostraPassword ? 'Nascondi chiave e frase' : 'Mostra chiave e frase'}
            </Pulsante>
          </>
        )}

        {passo === 2 && (
          <>
            <Avvertenza livello="grave">
              La password non viene salvata da nessuna parte e non passa dal server: non esiste un
              modo di recuperarla. Se la perdi, quel wallet è perso.
            </Avvertenza>

            <Campo
              etichetta="Password"
              testuale
              type={mostraPassword ? 'text' : 'password'}
              suggerimento="cifra i tre valori qui nel browser"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              errore={errorePassword}
              autoComplete="new-password"
              spellCheck={false}
            />
            <Campo
              etichetta="Ripeti la password"
              testuale
              type={mostraPassword ? 'text' : 'password'}
              value={conferma}
              onChange={(e) => setConferma(e.target.value)}
              errore={erroreConferma}
              autoComplete="new-password"
              spellCheck={false}
            />
            <Pulsante variante="nudo" onClick={() => setMostraPassword((v) => !v)}>
              {mostraPassword ? 'Nascondi le password' : 'Mostra le password'}
            </Pulsante>

            {password !== '' && password.length >= MINIMO_PASSWORD && password.length < CONSIGLIATO_PASSWORD && (
              <Avvertenza livello="cautela">
                Va bene, ma è corta: {password.length} caratteri. Su una cassaforte che nessuno
                può riaprire per te, una frase di quattro parole vale più di otto
                caratteri complicati.
              </Avvertenza>
            )}
          </>
        )}

        {passo === 3 && (
          <>
            <div className="sb-cs-riepilogo">
              <div className="sb-riga sb-cs-riepilogo__riga">
                <span className="sb-etichetta">Nome</span>
                <span>{nome.trim()}</span>
              </div>
              <div className="sb-riga sb-cs-riepilogo__riga">
                <span className="sb-etichetta">Indirizzo</span>
                <span className="sb-cs-mono">{indirizzo.trim()}</span>
              </div>
              <div className="sb-riga sb-cs-riepilogo__riga">
                <span className="sb-etichetta">Chiave privata</span>
                <span className="sb-cs-mono">{mascherato(chiave.trim())}</span>
              </div>
              <div className="sb-riga sb-cs-riepilogo__riga">
                <span className="sb-etichetta">Frase mnemonica</span>
                <span className="sb-cs-mono">
                  {mnemonica.trim() === '' ? 'nessuna' : `${mnemonica.trim().split(/\s+/).length} parole`}
                </span>
              </div>
            </div>

            <Avvertenza livello="nota">
              Tutti e tre i valori vengono cifrati qui, con la tua password, e riaperti subito per
              controllo prima di partire. Al server arriva solo testo cifrato: nemmeno l'indirizzo
              è leggibile a database, ed è il motivo per cui la lista mostra solo il nome.
            </Avvertenza>

            <label className="sb-cs-spunta">
              <input type="checkbox" checked={annotata} onChange={(e) => setAnnotata(e.target.checked)} />
              <span>
                Ho annotato la password in un posto sicuro. Ho capito che senza non si riapre.
              </span>
            </label>
          </>
        )}
      </div>
    </Modale>
  );
}
